/** Decides which link targets may be stored, rendered, and activated. */
export interface UrlPolicy {
  /** Lower-case schemes (without the colon) that may appear in stored and rendered links. */
  allowedSchemes: readonly string[];
  /** Application schemes routed through the host's link handler instead of the browser. */
  internalSchemes: readonly string[];
  /** Whether scheme-less targets such as `/path`, `#anchor`, or `page.html` are accepted. */
  allowRelative: boolean;
}

export const DEFAULT_URL_POLICY: UrlPolicy = {
  allowedSchemes: ["http", "https", "mailto", "tel"],
  internalSchemes: [],
  allowRelative: true,
};

export type UrlVerdict =
  | { ok: true; url: string; kind: "external" | "internal" | "relative" }
  | { ok: false; reason: "empty" | "scheme" | "malformed" };

const SCHEME = /^([a-z][a-z0-9+.-]*):/i;

// URL parsers drop control characters and whitespace, so `java\nscript:` must be normalized
// before the scheme is read.
function stripIgnoredByUrlParsers(url: string): string {
  let out = "";
  for (const char of url) {
    const code = char.codePointAt(0)!;
    if (code > 0x20 && code !== 0x7f) out += char;
  }
  return out;
}

export function checkUrl(input: string, policy: UrlPolicy = DEFAULT_URL_POLICY): UrlVerdict {
  const url = input.trim();
  if (url === "") return { ok: false, reason: "empty" };
  const match = SCHEME.exec(stripIgnoredByUrlParsers(url));
  if (match === null) {
    if (!policy.allowRelative) return { ok: false, reason: "scheme" };
    // Protocol-relative URLs inherit the page scheme, which is always http(s).
    return { ok: true, url, kind: url.startsWith("//") ? "external" : "relative" };
  }
  const scheme = match[1]!.toLowerCase();
  if (policy.internalSchemes.includes(scheme)) return { ok: true, url, kind: "internal" };
  if (!policy.allowedSchemes.includes(scheme)) return { ok: false, reason: "scheme" };
  if ((scheme === "http" || scheme === "https") && !URL.canParse(url)) {
    return { ok: false, reason: "malformed" };
  }
  return { ok: true, url, kind: "external" };
}

export function isSafeUrl(input: string, policy: UrlPolicy = DEFAULT_URL_POLICY): boolean {
  return checkUrl(input, policy).ok;
}

/** Adds `https://` to bare domains typed into the link editor; leaves everything else alone. */
export function normalizeTypedUrl(input: string): string {
  const url = input.trim();
  if (url === "" || SCHEME.test(url) || /^[/#?.]/.test(url)) return url;
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(url)) return `mailto:${url}`;
  if (/^[^\s/]+\.[^\s/]+/.test(url)) return `https://${url}`;
  return url;
}
